import { LegacyCard } from "../card/LegacyCard";
import { H1, H5, H6, SmallButton } from "@sellar-io/components";
import {
  calculateItemTotal,
  calculateVatTotal,
  formatCurrency,
} from "@sellar-io/common";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router";
import { twMerge } from "tailwind-merge";
import { useSellarUser } from "../../hooks";
import { useOrder } from "../../hooks/api/v2/order/useOrder";
import { useParamAsNumber } from "../../hooks/useParamAsNumber";
import { ORDER_DELIVERY_NOTE } from "../../constants/routes";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import {
  renderDeliveryDateTitle,
  renderTaxName,
} from "../../utils/sellarHelpers";
import { SafariPrintAlert } from "../alert/SafariPrintAlert";
import { Badge } from "../badge/Badge";
import { NavigationBar } from "../navigation/NavigationBar";
import { ProfilePicture } from "../profilePicture/ProfilePicture";
import { SectionHeader } from "../sectionHeader/SectionHeader";
import { SpinnerLegacy } from "..";
import { generateOrderNumber } from "./helpers";
import { OrderAddress } from "./OrderAddress";
import { OrderSummaryContainer } from "./OrderSummaryContainer";

export const OrderDeliveryNote = (): React.ReactElement => {
  const orderId = useParamAsNumber(ORDER_DELIVERY_NOTE, "orderId");

  const [searchParams] = useSearchParams();
  const { business } = useSellarCurrentBusiness();
  const { user } = useSellarUser();
  const navigate = useNavigate();

  const query = useOrder(orderId);

  const SELLAR_AWRS_NUMBER = "XDAW 000 0012 0248";

  useEffect(() => {
    if (query.data) {
      document.title = `${generateOrderNumber(query.data)}${
        query.data.Supplier.Business
          ? business?.Retailer
            ? ` - ${query.data.Supplier.Business.name}`
            : ` - ${query.data.Retailer.Business.name}`
          : ""
      }`;
    }
  }, [query.data, business?.Retailer]);

  return (
    <>
      <NavigationBar
        title={"Order Delivery Notes"}
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={() => navigate(-1)}
      />
      {query.isLoading ? (
        <SpinnerLegacy />
      ) : (
        query.isSuccess && (
          <div
            className="mx-auto flex max-w-[900px] flex-col gap-2 space-y-2 p-2 print:w-screen print:grayscale"
            style={{ zoom: "80%" }}
          >
            <SafariPrintAlert />
            <SmallButton
              className={"print:hidden"}
              onClick={() => window.print()}
            >
              Print
            </SmallButton>
            <div>
              <div className="flex justify-between">
                <img
                  src={query.data.Supplier.Business.logoUrl ?? ""}
                  className="my-auto block h-20 object-contain"
                  alt={query.data.Supplier.Business.name}
                />
                <div className="flex flex-col select-text">
                  {query.data.paymentType === "sellarCredit" ? (
                    <>
                      <span>WAFFLE TECH LIMITED</span>
                      <span>Preston Park House</span>
                      <span>Brighton</span>
                      <span>BN1 6SB, GB</span>
                    </>
                  ) : (
                    <>
                      <span>
                        {query.data.Supplier.Business.InvoiceAddress.name}
                      </span>
                      <span>
                        {query.data.Supplier.Business.InvoiceAddress.street}
                      </span>
                      <span>
                        {query.data.Supplier.Business.InvoiceAddress.city}
                      </span>
                      <span>
                        {query.data.Supplier.Business.InvoiceAddress.region}
                      </span>
                      <span>
                        {query.data.Supplier.Business.InvoiceAddress.postalCode}
                      </span>
                      <span>
                        {query.data.Supplier.Business.InvoiceAddress.country}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-4 text-sm">
                <div>
                  <span className="font-medium">Supplier Name</span>
                  <p>{query.data.Supplier.Business.name}</p>
                </div>
                <div className="text-right">
                  <span>
                    {user?.name}
                    {query.data.Supplier.Business.phoneNumber && (
                      <span> - {query.data.Supplier.Business.phoneNumber}</span>
                    )}
                  </span>
                </div>
                <div>
                  <span className="font-medium">AWRS</span>
                  <p>
                    {query.data.paymentType === "sellarCredit"
                      ? SELLAR_AWRS_NUMBER
                      : query.data.Supplier.Business.awrsNumber}
                  </p>
                </div>
                <div className="text-right">
                  {query.data.Supplier.Business.invoiceEmails?.length > 0 && (
                    <span>
                      {query.data.Supplier.Business.invoiceEmails.join(", ")}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <LegacyCard>
              <SectionHeader
                icon={
                  business?.Supplier ? (
                    <ProfilePicture
                      logoUrl={query.data.Retailer.Business.logoUrl}
                      name={query.data.Retailer.Business.name}
                    />
                  ) : (
                    <ProfilePicture
                      logoUrl={query.data.Supplier.Business.logoUrl}
                      name={query.data.Supplier.Business.name}
                    />
                  )
                }
                title={
                  <H1
                    className={twMerge(business?.Supplier && "cursor-pointer")}
                  >
                    {business?.Supplier
                      ? query.data.Retailer.Business.name
                      : query.data.Supplier.Business.name}
                  </H1>
                }
                subtitle={<H6>{generateOrderNumber(query.data)}</H6>}
              />
              <div className="flex flex-wrap gap-2">
                <Badge className="text-secondary">
                  Ordered on {dayjs(query.data.createdAt).format("DD/MM/YY")}
                </Badge>
                {query.data.confirmedAt && query.data.paymentDue && (
                  <Badge className="text-secondary">
                    Payment due on{" "}
                    {dayjs(query.data.paymentDue).format("DD/MM/YY")}
                  </Badge>
                )}
                {query.data.deliveryPlannedAt && (
                  <Badge className="text-secondary">
                    {renderDeliveryDateTitle(query.data.fulfillmentType)} on{" "}
                    {dayjs(query.data.deliveryPlannedAt).format("DD/MM/YY")}
                  </Badge>
                )}
              </div>
            </LegacyCard>

            <OrderSummaryContainer
              items={query.data.OrderItems.map((orderItem) => ({
                description: orderItem.Product.name ?? "",
                quantity: orderItem.quantity,
                cost: formatCurrency(orderItem.price),
                sku: orderItem.Product.sku,
                promotionAppliedAt: orderItem.promotionAppliedAt,
              }))}
              priceProps={
                searchParams.get("pricing")
                  ? {
                      delivery:
                        business?.Supplier || query.data.stripeInvoiceId
                          ? formatCurrency(
                              query.data.deliveryCharge,
                              query.data.currency,
                            )
                          : undefined,
                      subTotal: query.data.OrderItems
                        ? formatCurrency(
                            calculateItemTotal(query.data.OrderItems),
                            query.data.currency,
                          )
                        : "",
                      discount:
                        query.data.discountAmount > 0
                          ? {
                              value: formatCurrency(
                                query.data.discountAmount,
                                query.data.currency,
                              ),
                              calculatedTotal: formatCurrency(
                                query.data.discountAmount,
                                query.data.currency,
                              ),
                            }
                          : undefined,
                      tax: {
                        name: renderTaxName(query.data.currency),
                        percentage: query.data.vatPercentage.toString(),
                        total: formatCurrency(
                          calculateVatTotal({
                            ...query.data,
                            orderItems: query.data.OrderItems,
                          }),
                          query.data.currency,
                        ),
                      },
                      total: formatCurrency(
                        query.data.price,
                        query.data.currency,
                      ),
                    }
                  : undefined
              }
            />

            <LegacyCard className="flex justify-between gap-2">
              <div>
                <OrderAddress
                  address={query.data.DeliveryAddress}
                  title="Delivery Address"
                />
              </div>
            </LegacyCard>

            {query.data.deliveryNotes && (
              <LegacyCard>
                <H5>Delivery instructions</H5>
                <H6 className="mt-2 break-words whitespace-pre-wrap">
                  {query.data.deliveryNotes}
                </H6>
              </LegacyCard>
            )}

            <LegacyCard className="flex gap-2">
              {["Signature", "Date", "Print Name", "Time"].map((label) => (
                <div key={label} className="block shrink">
                  <label>{label}:</label>
                  <input
                    className="w-full rounded-md border-2 p-2"
                    placeholder={label}
                  />
                </div>
              ))}
            </LegacyCard>
          </div>
        )
      )}
    </>
  );
};
